import React, { useCallback, useContext, useMemo } from 'react'

import BookmarkCard from '../BookmarkCardNew/BookmarkCard'
import BookmarkLocation from '../BookmarkCardNew/BookmarkLocation'
import PriceDetails from '../BookmarkCardNew/PriceDetails'

import BodyText from 'components/Luxkit/Typography/BodyText'
import ClickTrackerContext from 'contexts/ClickTrackerContext'
import GeoContext from 'contexts/geoContext'
import useImpressionHandler from 'hooks/useImpressionHandler'
import { mapExperienceToBookingView } from 'lib/experienceUtils'
import { getImageUrl } from 'lib/imageUtils'
import noop from 'lib/noop'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { addQueryParamsToPath } from 'lib/urlUtils'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'

type ExperienceBookmarkCardPriceDetailsProps = {
  view: App.ExperienceBookingView
  currencyCode: string
}
const ExperienceBookmarkCardPriceDetails = ({
  view,
  currencyCode,
}: ExperienceBookmarkCardPriceDetailsProps) => {
  return (
    <PriceDetails
      price={view.price}
      discount={0}
      priceLabel="From"
      totalLabel=""
      discountDisplayType="none"
      priceDisplayType="taxesAndFeesInclusive"
      currencyCode={currencyCode}
    />
  )
}

export type ExperienceBookmarkCardProps = {
  experience: App.ExperienceOffer
  onClick?: () => void
}

const ExperienceBookmarkCard = ({
  experience,
  onClick = noop,
}: ExperienceBookmarkCardProps) => {
  const experienceView = useMemo(() => {
    if (experience) {
      return mapExperienceToBookingView(experience)
    }

    return null
  }, [experience])
  const { image, title } = experienceView ?? {}

  // always prefer ID over URL
  const imageUrl = image?.id
    ? getImageUrl(image?.id, { width: '320px' })
    : image?.url
  const source = useTripClientSource()
  const offerUrl = addQueryParamsToPath(getExperienceOfferPageURL(experience), {
    source,
  })
  const { currentCurrency } = useContext(GeoContext)

  const ref = useImpressionHandler(experience.id)

  const clickTracker = useContext(ClickTrackerContext)
  const onClickHandler = useCallback(() => {
    clickTracker(experience.id)
    onClick()
  }, [clickTracker, experience.id, onClick])

  // We shouldn't need to check this, but the OfferTypeLabel
  // falls back to 'Lux Exclusive' for always-on experience
  // offers.
  const shouldShowOfferTypeLabel = experienceView?.leExclusive
  return (
    experienceView && (
      <BookmarkCard
        ref={ref}
        title={title}
        body={
          experience.vendor ? (
            <BodyText variant="small">{experience.vendor.name}</BodyText>
          ) : undefined
        }
        bookmarkButton={<ExperienceBookmarkButton offer={experience} />}
        location={
          <BookmarkLocation
            variant="with_experience"
            startLocation={experience?.location.name}
            typeOfExperience={experience?.primaryCategory?.name}
          />
        }
        imageUrl={imageUrl}
        productType={
          shouldShowOfferTypeLabel ? experience.productType : undefined
        }
        offerPriceDetails={
          <ExperienceBookmarkCardPriceDetails
            view={experienceView}
            currencyCode={currentCurrency}
          />
        }
        offerUrl={offerUrl}
        onClick={onClickHandler}
        urgencyLabels={[]}
        isSoldOut={experience.status !== 'ONLINE'}
      />
    )
  )
}

export default ExperienceBookmarkCard
